import Papa from 'papaparse';

/**
 * Analisa um arquivo CSV para extrair dados de postes que correspondem a um protocolo de projeto específico.
 * A função é flexível e tenta encontrar os cabeçalhos necessários (protocolo, latitude, longitude, endereço)
 * mesmo que tenham nomes ligeiramente diferentes.
 *
 * @param {File} file - O objeto de arquivo CSV a ser analisado.
 * @param {string} projectProtocol - O protocolo do projeto usado para filtrar os postes relevantes.
 * @returns {Promise<Array<Object>>} Uma Promise que resolve com um array de objetos de postes 
 * ou rejeita com um erro se a análise falhar ou se os dados forem inválidos.
 */
export const parsePolesFromCSV = (file, projectProtocol) => {
    // Retorna uma nova Promise para lidar com a natureza assíncrona da análise de arquivos.
    return new Promise((resolve, reject) => {
        // Inicia o processo de análise com PapaParse.
        Papa.parse(file, {
            header: true,          // Trata a primeira linha como cabeçalho.
            skipEmptyLines: true,  // Ignora linhas em branco no arquivo.

            // Função de callback que é executada quando a análise é concluída com sucesso.
            complete: (results) => {
                try {
                    // Pega os nomes dos campos (cabeçalhos) e os converte para minúsculas para uma correspondência insensível a maiúsculas.
                    const headers = results.meta.fields.map(h => h.trim().toLowerCase());
                    
                    /**
                     * Função auxiliar para encontrar um cabeçalho na lista de cabeçalhos do CSV.
                     * @param {Array<string>} possibleNames - Uma lista de possíveis nomes para o cabeçalho (ex: ['latitude', 'lat']).
                     * @returns {string|null} O nome do cabeçalho original encontrado ou nulo se não for encontrado.
                     */
                    const findHeader = (possibleNames) => {
                        for (const name of possibleNames) {
                            // Procura um cabeçalho que corresponda a um dos nomes possíveis.
                            const found = headers.find(h => h.replace(/\s/g, '') === name);
                            if (found) {
                                // Retorna o nome do cabeçalho original do arquivo.
                                return results.meta.fields[headers.indexOf(found)];
                            }
                        }
                        return null;
                    };

                    // Tenta encontrar os nomes exatos dos cabeçalhos para as colunas necessárias.
                    const protocolHeader = findHeader(['protocolodelicenciamento', 'protocolo de licenciamento', 'protocolo']);
                    const latHeader = findHeader(['latitude', 'lat']);
                    const lonHeader = findHeader(['longitude', 'lon', 'lng']);
                    const addressHeader = findHeader(['endereço', 'endereco', 'address']);

                    // Se qualquer um dos cabeçalhos essenciais (protocolo, lat, lon) não for encontrado, rejeita a Promise com um erro.
                    if (!protocolHeader || !latHeader || !lonHeader) {
                        return reject(new Error("Não foi possível encontrar as colunas necessárias (Protocolo, Latitude, Longitude) no CSV."));
                    }

                    // Filtra as linhas do CSV para encontrar apenas aquelas que contêm o protocolo do projeto.
                    const matchingRows = results.data.filter(row => {
                        const poleProtocols = (row[protocolHeader] || '').toLowerCase();
                        return poleProtocols.includes(projectProtocol.trim().toLowerCase());
                    });
                    
                    // Se nenhuma linha corresponder, informa o usuário.
                    if (matchingRows.length === 0) {
                         return reject(new Error("Nenhuma linha no CSV corresponde ao protocolo do projeto informado."));
                    }

                    // Mapeia as linhas correspondentes para o formato de objeto de poste desejado.
                    const polesForProject = matchingRows.map(row => {
                        // Converte os valores de latitude e longitude para números, lidando com vírgulas como separadores decimais.
                        const lat = parseFloat(String(row[latHeader] || '').replace(',', '.'));
                        const lon = parseFloat(String(row[lonHeader] || '').replace(',', '.'));
                        
                        // Se a latitude ou longitude não for um número válido, retorna nulo para ser filtrado posteriormente.
                        if (isNaN(lat) || isNaN(lon)) return null;

                        // Retorna o objeto de poste formatado.
                        return {
                            latitude: lat,
                            longitude: lon,
                            endereco: row[addressHeader] || 'N/A', // Usa 'N/A' se o endereço não estiver presente.
                            protocoloLicenciamento: row[protocolHeader] || 'N/A'
                        };
                    }).filter(Boolean); // Remove quaisquer entradas nulas resultantes de coordenadas inválidas.

                    // Se, após a filtragem, não houver postes válidos, rejeita a Promise.
                    if (polesForProject.length === 0) {
                       return reject(new Error("Os postes encontrados para este protocolo possuem coordenadas inválidas."));
                    }
                    
                    // Resolve a Promise com a lista de postes válidos.
                    resolve(polesForProject);

                } catch (e) {
                    // Captura quaisquer outros erros durante o processamento e os rejeita.
                    reject(e);
                }
            },
            // Função de callback para lidar com erros durante a própria análise do arquivo.
            error: (err) => {
                reject(new Error("Falha ao ler o arquivo CSV. Verifique o formato do arquivo."));
            }
        });
    });
};
